import { useTranslation } from "react-i18next";
import camelCase from "lodash/camelCase";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { EntityIcons } from "../../../../../utils/GlobalIcons";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { useAppSelector } from "../../../../../redux/store";
import { reportSliceSelectors } from "../../../../../redux/report/report.selector";
import { dealMetricKeys, widgetTitleIconsAndColors } from "./types";
import { ThemeType } from "../../../../../theme/Theme";
import { Spacing } from "../../../../../theme/Spacing";
import {
  getRangeLabel,
  onViewEntitiesFromCards,
} from "../../../../../utils/Reports";
import { toCurrency } from "../../../../../utils/pricingUtils";
import { KanbanCardDateField } from "../../../../../model/kanban/types/KanbanCardDateField.enum";
import { KanbanColumnStageType } from "../../../../../model/kanban/types/KanbanColumnStageType.enum";
import { ReportKinds } from "../../../../../model/reports/types/ReportsKinds.enum";
import { ActivityEntityKind } from "../../../../../model/activity/types/ActivityEntityKind.enum";
import { ReportsSummarySection, SummaryMetric } from "./ReportsSummarySection";
import { ReportTrendChart } from "../../ReportTrendChart";
import { ReportRateTile } from "../../ReportRateTile";

const ValueGrid = styled(Flex)`
  gap: ${Spacing.l};
  flex-wrap: wrap;
`;

const ValueCell = styled(Flex)`
  flex-direction: column;
  min-width: 8rem;
`;

const stageByReportKind: Partial<Record<ReportKinds, KanbanColumnStageType>> =
  {
    [ReportKinds.DEAL_WON]: KanbanColumnStageType.CLOSED_WON,
    [ReportKinds.DEAL_LOST]: KanbanColumnStageType.CLOSED_LOST,
    [ReportKinds.DEAL_TO_COMPETITION]:
      KanbanColumnStageType.CLOSED_TO_COMPETITION,
  };

export const ReportsSummaryDealTab: React.FC = () => {
  const { t } = useTranslation();
  const reports = useAppSelector(reportSliceSelectors.selectReport);
  const navigate = useNavigate();

  const rangeLabel = getRangeLabel(reports, t);
  const dealValue = reports.dealValue || [];

  // "Created in range" and "closed in range" are different windows over
  // different deals, so the total is shown as its own figure rather than as a
  // whole the closed counts are subtracted from.
  const metrics: SummaryMetric[] = dealMetricKeys.map((key) => {
    const isTotal = key === ReportKinds.DEAL;

    return {
      key,
      title: t(`reports.${camelCase(key)}.title`),
      subtitle: `${t(
        isTotal
          ? "reports.metric.createdInRange"
          : "reports.metric.closedInRange",
      )} · ${rangeLabel}`,
      value: reports.metrics?.[key] ?? 0,
      previousValue: reports.previousMetrics?.[key],
      higherIsWorse:
        key === ReportKinds.DEAL_LOST ||
        key === ReportKinds.DEAL_TO_COMPETITION,
      color: widgetTitleIconsAndColors[key].color as keyof ThemeType,
      icon: widgetTitleIconsAndColors[key].icon,
      emphasize: isTotal,
      onView: () =>
        onViewEntitiesFromCards(
          {
            range: reports?.range,
            scope: reports?.scope,
            dateField: isTotal
              ? KanbanCardDateField.CREATED_AT
              : KanbanCardDateField.CLOSED_AT,
            stage: stageByReportKind[key],
          },
          ReportKinds.DEAL,
          navigate,
        ),
    };
  });

  return (
    <ReportsSummarySection
      title={t("common.deals")}
      icon={EntityIcons.Deals}
      info={t("reports.tabs.summary.subtab.deal.info")}
      metrics={metrics}
      columns={4}
      footer={
        <Flex flexDirection="column" gap="l">
          {dealValue.length ? (
            <Flex flexDirection="column" gap="xs">
              <Text fontWeight="bold">{t("reports.dealValue.title")}</Text>
              {/* One row, in the workspace's current currency: the API has no
                  exchange-rate pipeline, so amounts are summed as stored
                  rather than converted or split out per original currency. */}
              {dealValue.map((row) => {
                return (
                  <ValueGrid key={row.currency}>
                    <ValueCell>
                      <Text fontSize="s" color="darkerSub">
                        {t("reports.dealValue.won")} · {row.currency}
                      </Text>
                      <Text fontSize="l" color="success">
                        {toCurrency(row.won * 100, row.currency)}
                      </Text>
                    </ValueCell>
                    <ValueCell>
                      <Text fontSize="s" color="darkerSub">
                        {t("reports.dealValue.lost")} · {row.currency}
                      </Text>
                      <Text fontSize="l" color="danger">
                        {toCurrency(row.lost * 100, row.currency)}
                      </Text>
                    </ValueCell>
                    <ValueCell>
                      <Text fontSize="s" color="darkerSub">
                        {t("reports.dealValue.created")} · {row.currency}
                      </Text>
                      <Text fontSize="l">
                        {toCurrency(row.created * 100, row.currency)}
                      </Text>
                    </ValueCell>
                  </ValueGrid>
                );
              })}
            </Flex>
          ) : null}

          <ReportRateTile
            title={t("reports.rate.dealWin")}
            hint={t("reports.rate.dealWin.hint")}
            value={reports.rates?.dealWin ?? null}
            tone="success"
          />

          <ReportTrendChart kind={ActivityEntityKind.KANBAN_CARD} />
        </Flex>
      }
    />
  );
};
